/**
 * X3 — additional discount + A/B split on a sales order (Phase 2 plan 11). Rendered inside SalesOrderDetailPage with
 * that page's card classes. Figures always come from the server; the card never computes money itself.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { formatIstDay } from '../admin/splitMath';

interface Rule { version: number; anchorDiscountPct: number; anchorSharePct: number; slopePerPct: number; minDiscountPct: number; maxDiscountPct: number; effectiveFrom: string }
interface Snapshot { discountPct: number; servicePartnerId: string; scopeText: string; consentLine: string; ruleVersion: number; ruleEffectiveFrom: string; sharePct: number; amountAPaise: number; amountBPaise: number }
interface View { eligible: { exGstPaise: number }; split: Snapshot | null; activeRule: Rule | null; locked: boolean; lockedReason: string | null }
interface Figures { ruleVersion: number; sharePct: number; amountAPaise: number; amountBPaise: number }
interface Partner { id: string; name: string; isActive: boolean }

const CARD = 'rounded-xl border border-gray-200 bg-white p-5';
const H3 = 'mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider';
const FIELD = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm';
const errorOf = (err: any, fallback: string) => err?.response?.data?.error ?? fallback;

export function AdditionalDiscountSplitCard({ salesOrderId }: { salesOrderId: string }) {
  const [view, setView] = useState<View | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [discount, setDiscount] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [scope, setScope] = useState('');
  const [preview, setPreview] = useState<Figures | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [v, p] = await Promise.all([api.get(`/split/sales-orders/${salesOrderId}`), api.get('/split/service-partners')]);
      const data: View = v.data.data;
      if (!data?.eligible) { setView(null); return; }
      setView(data);
      setPartners(p.data?.data ?? []);
      if (data.split) {
        setDiscount(String(data.split.discountPct));
        setPartnerId(data.split.servicePartnerId);
        setScope(data.split.scopeText);
      }
    } catch {
      setView(null);
    }
  }, [salesOrderId]);
  useEffect(() => { load(); }, [load]);

  if (!view) return null;
  if (view.eligible.exGstPaise === 0 && !view.split) return null;

  const rule = view.activeRule;
  const shown: Figures | null = preview ?? view.split;
  const ruleLabel = view.split && !preview
    ? `Split · rule v${view.split.ruleVersion} from ${formatIstDay(view.split.ruleEffectiveFrom)}`
    : rule ? `Split · rule v${rule.version} from ${formatIstDay(rule.effectiveFrom)}` : 'Split · no rule in force';
  const partnerName = partners.find((p) => p.id === partnerId)?.name;

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (err) { setError(errorOf(err, fallback)); } finally { setBusy(false); }
  };

  const doPreview = () => run(async () => {
    const r = await api.post(`/split/sales-orders/${salesOrderId}/preview`, { discountPct: Number(discount) });
    setPreview(r.data.data);
  }, 'Preview failed');

  const doSave = () => run(async () => {
    await api.put(`/split/sales-orders/${salesOrderId}`, { discountPct: Number(discount), servicePartnerId: partnerId, scopeText: scope });
    setPreview(null);
    await load();
  }, 'Saving the split failed');

  const doClear = () => run(async () => {
    await api.delete(`/split/sales-orders/${salesOrderId}`);
    setPreview(null);
    setDiscount(''); setPartnerId(''); setScope('');
    await load();
  }, 'Removing the split failed');

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="so-split-card">
      <div className={CARD}>
        <h3 className={H3}>Additional discount</h3>
        <div className="space-y-3">
          <label className="block text-xs text-gray-600">
            Discount %
            <input aria-label="Discount %" type="number" step="0.01" min={rule?.minDiscountPct ?? 0} max={rule?.maxDiscountPct ?? 7.5}
              className={FIELD} value={discount} disabled={view.locked} onChange={(e) => { setDiscount(e.target.value); setPreview(null); }} />
          </label>
          <label className="block text-xs text-gray-600">
            Service partner
            <select aria-label="Service partner" className={FIELD} value={partnerId} disabled={view.locked} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">Pick a partner</option>
              {partners.filter((p) => p.isActive).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          {partners.length === 0 && <p className="text-xs text-amber-700">Add a service partner on Discount Policies first.</p>}
          <label className="block text-xs text-gray-600">
            Service scope (required)
            <textarea aria-label="Service scope (required)" rows={3} className={FIELD} value={scope} disabled={view.locked} onChange={(e) => setScope(e.target.value)} />
          </label>
          {view.locked ? (
            <p className="text-xs text-gray-500">{view.lockedReason}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button onClick={doPreview} disabled={busy || !discount} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Preview</button>
              <button onClick={doSave} disabled={busy || partners.length === 0} className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {busy && <Loader2 size={14} className="animate-spin" />}Save split
              </button>
              {view.split && <button onClick={doClear} disabled={busy} className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">Remove split</button>}
            </div>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      </div>

      <div className={CARD}>
        <h3 className={H3}>{ruleLabel}</h3>
        {shown ? (
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Company share</span><span className="font-medium text-gray-900">{`${shown.sharePct.toFixed(2)}%`}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Amount A · invoiced</span><span className="font-medium text-gray-900">{formatINR(shown.amountAPaise / 100)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Amount B · partner</span><span className="font-medium text-gray-900">{formatINR(shown.amountBPaise / 100)}</span></div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Enter a discount and press Preview.</p>
        )}
        {rule && <p className="mt-3 text-xs text-gray-400">{`share(d) = ${rule.anchorSharePct} − (d − ${rule.anchorDiscountPct}) × ${rule.slopePerPct}`}</p>}
        {(view.split?.consentLine || partnerName) && (
          <p className="mt-2 text-xs text-gray-600">
            {view.split && !preview ? view.split.consentLine : `Services listed are provided and charged by ${partnerName}, not Sun Stellar Industries.`}
          </p>
        )}
        <p className="mt-2 text-xs text-gray-400">Only Amount A goes to the invoice, IRN and e-way bill.</p>
      </div>
    </div>
  );
}
